import { useMutation } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { getAuthValue } from "../../authModule/hooks/useAuthValue";
import { showToast } from "../../../utils/ToastEmitter";
import { COPILOT_CHAT_REQUEST } from "../types/RequestTypes";
import { COPILOT_CHAT_RESPONSE } from "../types/ResponseTypes";
import {
  appendCopilotAssistantMessage,
  getCopilotValue,
  setPendingSuggestions,
  updateCopilotValue,
} from "./useCopilotValue";
import {
  mapDataCategoryToTags,
  mapProminentEntitiesToTags,
} from "../helpers/copilotCategoryHelper";

const COPILOT_MAX_HISTORY_TURNS = 0;

async function sendCopilotChat(message: string) {
  const { hospitalId, tenantId } = getAuthValue();
  const { sessionId } = getCopilotValue();

  const req: COPILOT_CHAT_REQUEST = {
    client_id: process.env.REACT_APP_COPILOT_CLIENT_ID ?? "",
    client_secret: process.env.REACT_APP_COPILOT_CLIENT_SECRET ?? "",
    agent_id: process.env.REACT_APP_COPILOT_AGENT_ID ?? "",
    session_id: sessionId ?? "",
    message,
    max_history_turns: COPILOT_MAX_HISTORY_TURNS,
  };

  // Use raw axios — copilot is a separate server that uses its own client
  // credentials auth, not the admin JWT token.
  const response = await axios.post<COPILOT_CHAT_RESPONSE>(
    process.env.REACT_APP_COPILOT_CHAT_URL ?? "",
    req,
    {
      headers: {
        "Content-Type": "application/json",
        "X-Hospital-Id": hospitalId ?? "",
        "X-Tenant-Id": tenantId ?? "",
        "X-Session-Id": sessionId ?? "",
        "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    }
  );
  // Bundle the session ID so onSuccess can detect if a new session was started
  // while this request was in flight and discard the stale response.
  return { data: response.data, sessionId };
}

export default function useCopilotChatMutation() {
  return useMutation({
    mutationFn: sendCopilotChat,
    onSuccess: (res) => {
      // Discard the response if the session changed while this request was in flight.
      if (getCopilotValue().sessionId !== res.sessionId) return;
      const details = res.data?.tool_calls?.[0]?.result?.details;
      const categoryTags = mapDataCategoryToTags(details?.data_category) ?? [];
      const entityTags = mapProminentEntitiesToTags(details?.prominent_entities);
      const tags = [...categoryTags, ...entityTags];
      appendCopilotAssistantMessage(
        res.data.tool_calls?.[0]?.result?.answer ?? "",
        tags.length > 0 ? tags : undefined
      );
      const suggestions = details?.suggestions ?? [];
      if (suggestions.length > 0) {
        setPendingSuggestions(suggestions);
      }
    },
    onError: (_error: AxiosError) => {
      if (getCopilotValue().isAwaitingResponse) {
        updateCopilotValue({ isAwaitingResponse: false });
        showToast("Something_went_wrong", "error");
      }
    },
  });
}
